import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
const blank = { serial: "", internal_number: "", received_date: "", pin: "", puk: "", phone_id: NONE, station_id: "", notes: "" };
const F = ({ label, children, span }) => <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;

export const SimDialog = ({ item, stations, phones, isAdmin, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) setF({ ...blank, station_id: stations[0]?.id || "", ...item, phone_id: item.phone_id || NONE, received_date: item.received_date || "" });
  }, [item, stations]);
  if (!item) return null;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const st = isAdmin ? f.station_id : phones[0]?.station_id;
  const opts = phones.filter((p) => !isAdmin || p.station_id === st);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...Object.fromEntries(Object.keys(blank).map((k) => [k, f[k]])), phone_id: f.phone_id === NONE ? null : f.phone_id, received_date: f.received_date || null };
      if (item.id) await api.put(`/phones/sims/${item.id}`, payload); else await api.post("/phones/sims", payload);
      toast.success("SIM salvata");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="sim-dialog">
        <DialogHeader>
          <DialogTitle>{item.id ? `SIM ${item.serial}` : "Nuova SIM"}</DialogTitle>
          <DialogDescription>Associando un telefono, il suo campo IMEI SIM viene aggiornato con il seriale.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Seriale SIM" span><Input value={f.serial} onChange={set("serial")} data-testid="sim-serial-input" /></F>
          <F label="Numero interno"><Input value={f.internal_number} onChange={set("internal_number")} data-testid="sim-internal-input" /></F>
          <F label="Data arrivo (facoltativa)"><Input type="date" value={f.received_date} onChange={set("received_date")} data-testid="sim-received-input" /></F>
          <F label="PIN"><Input value={f.pin} onChange={set("pin")} inputMode="numeric" data-testid="sim-pin-input" /></F>
          <F label="PUK"><Input value={f.puk} onChange={set("puk")} inputMode="numeric" data-testid="sim-puk-input" /></F>
          {isAdmin && <F label="Stazione" span>
            <Select value={f.station_id || undefined} onValueChange={(v) => setF((p) => ({ ...p, station_id: v, phone_id: NONE }))}>
              <SelectTrigger data-testid="sim-station-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>}
          <F label="Telefono associato" span>
            <PlateCombobox value={f.phone_id} onChange={(v) => setF((p) => ({ ...p, phone_id: v }))} testId="sim-phone-select" placeholder="Seleziona telefono" searchPlaceholder="Cerca IMEI o modello..."
              options={[{ value: NONE, label: "— Nessun telefono" }, ...opts.map((p) => ({ value: p.id, label: `${p.imei}${p.internal_number ? ` · int. ${p.internal_number}` : ""} · ${p.brand || ""} ${p.model || ""}${p.phone_number ? ` · ${p.phone_number}` : ""}` }))]} />
          </F>
          <F label="Note" span><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="sim-notes-input" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || f.serial.trim().length < 4 || (isAdmin && !f.station_id)} data-testid="sim-save-button">{busy ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
